import React, { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, arrayOf, QueryProvider,
  shape, useDataModel, extend, number, customType, useLazyDataModel
} from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { DrawerHeader } from '@one-thd/sui-atomic-components';
import { CreateAProjectContent } from '@thd-olt-component-react/project-forms';
import { MyStoreForm, DeliveryZipcodeForm } from '@thd-olt-component-react/project-localization';
import { DefaultScreen } from '../../common/DefaultScreen';
import { SelectGroup } from './SelectGroup';
import { MiniPip } from '../../common/mini-pip/MiniPip';
import { AddToContext } from '../../AddToContext';
import {
  SCREENS, HEADER_TEXT, ADD_ITEM_NEW_PROJECT, ADD_ITEMS_PROJECT_NEW_GROUP, ADD_ITEMS_PROJECT_EXISTING_GROUP,
  TRACK
} from '../../AddToConstants';
import { useProjects, projectsModel, useProjectDetails, projectDetailsModel } from '../../hooks/useProjects';
import { AddToLoading } from '../../common/addto-loading/AddToloading';
import { SelectProject } from './SelectProject';
import { onLocalizationUpdate, getDrawerTitle, shouldShowBack, triggerAnalytics } from '../../AddToHelpers';
import { CopyList } from '../copy-list/CopyList';
import { StarterLists } from '../list/StarterLists';
import { IntentsPreview } from '../../common/intents-preview/IntentsPreview';
import { useAddNewIntentsProject } from '../../hooks/useAddNewIntentsProject';

export const AddToProject = ({
  projectId,
  products: productsProp = [],
  initialLoad,
  setInitialLoad,
  showBackButton,
  productPodItemData,
}) => {
  const {
    storeId,
    screen,
    setScreen,
    handleClose,
    selectedGroup,
    setSelectedGroup,
    selectedProductDetails,
    showSuccessMessage,
    setShowSuccessMessage,
    setSelectedProductDetails,
    addToType,
    atcDrawerType,
    setAtcDrawerType,
    previousScreen
  } = useContext(AddToContext);
  // Flag to fire analytic if an item has been added to a project with a new project
  const isProjectCreated = useRef(false);
  const isIntent = Boolean(!productPodItemData?.itemId && productPodItemData?.groupId) || false;
  const [products, setProducts] = useState(productsProp);
  const [selectedProject, setSelectedProject] = useState({ projectId, projectName: '' });
  const { svocID, customerType } = useThdCustomer();
  const isMultiAdd = products?.length > 1;
  const itemId = selectedProductDetails?.itemId || products[0]?.itemId || productPodItemData?.itemId;

  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: isMultiAdd || !products[0]?.itemId
  });

  const pageSize = 25;
  const [page, setPage] = useState(1);
  const [loadedProjects, setLoadedProjects] = useState([]);

  const { product: singleProductData = {} } = data || selectedProductDetails?.productData || {};
  const {
    storeSkuNumber, omsThdSku, specialOrderSku
  } = singleProductData?.identifiers
    || selectedProductDetails?.productData?.identifiers
    || selectedProductDetails?.identifiers
    || {};
  const singleItemSku = storeSkuNumber || omsThdSku || specialOrderSku;
  const { pricing } = singleProductData;
  const basePrice = pricing?.value;
  const quantity = selectedProductDetails?.quantity || products[0]?.quantity;
  const { projectData, projectLoading, projectRefetch } = useProjectDetails({
    projectId: selectedProject.projectId
  });
  const {
    projectName = '',
    propertyType,
    addresses,
    defaultListId,
    defaultListMutationKey,
    itemGroups,
    assortmentZip,
    preferredStore,
  } = projectData?.projectDetails || {};
  const assortmentZipId = addresses?.find((address) => address.addressType === 'ASSORTMENT_ZIP')?.id || null;
  const [keyword, setKeyword] = useState(productPodItemData?.searchKeyword || '');
  const selectedGroupId = useRef(null);
  const {
    PROJECT_SELECT_PROJECT,
    DEFAULT,
    PROJECT_CREATE_PROJECT,
    PROJECT_SELECT_GROUP,
    MINI_PIP_BASE,
    MINI_PIP_PRODUCT_OVERVIEW,
    MINI_PIP_PRODUCT_SPECS,
    MINI_PIP_PRODUCT_REVIEWS,
    MINI_PIP_FULL,
    LOADING,
    MY_STORE,
    DELIVERY_ZIP,
    STARTER_LISTS,
    GENERATED_LIST_INTENTS_PREVIEW
  } = SCREENS;

  const { handleCreateNewIntents } = useAddNewIntentsProject({ projectId: selectedProject.projectId, projectRefetch });
  const aiListBuilderData = {
    refId: selectedProject.projectId,
    title: selectedProject?.projectName,
    onNewIntentsSave: handleCreateNewIntents,
    refType: 'PROJECT'
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-project.ready');
  }, []);

  useEffect(() => {
    if (selectedProject?.projectId) {
      projectRefetch();
    }
  }, [selectedProject?.projectId]);

  useEffect(() => {
    if (productsProp?.length > 0) {
      const getValidSku = (skus) => {
        if (typeof skus === 'string') {
          return skus;
        }
        const {
          storeSkuNumber: storeSkuNumberFromSkus, omsThdSku: omsThdSkuFromSkus, specialOrderSku: specialOrderSkuFromSkus
        } = skus;
        return storeSkuNumberFromSkus || omsThdSkuFromSkus || specialOrderSkuFromSkus;
      };
      if (productsProp?.length > 1) {
        const productData = productsProp.map((itemData) => ({
          ...itemData,
          sku: getValidSku(itemData.sku)
        })).filter((item) => item?.sku);
        setProducts(productData);
      } else {
        setProducts(productsProp);
      }
    }
  }, [productsProp]);

  const { projects, error, refetch, loading } = useProjects({
    page,
    pageSize,
    accountId: svocID,
    filter: [{ searchBy: 'STATUS', search: 'Active' }],
    sort: [{ sortBy: 'STATUS', sortOrder: 'ASC' }]
  });

  const projectsCount = projects?.projects?.totalProjects || 0;

  useEffect(() => {
    refetch();
  }, [svocID, page]);

  const [hasLoadedProjects, setHasLoadedProjects] = useState(false);

  useEffect(() => {
    if (!loading) {
      setLoadedProjects((prevLoadedProjects) => {
        return [...prevLoadedProjects, ...projects?.projects?.projects || []];
      });
      setHasLoadedProjects(true);
    }
  }, [projects]);

  useEffect(() => {
    if (!initialLoad || !products?.length) return;

    const isLoading = loading || !hasLoadedProjects;
    const projectsExist = Boolean(loadedProjects?.length);

    if (isLoading && !projectsExist) {
      setScreen(LOADING);
    } else if (projectsExist) {
      setScreen(PROJECT_SELECT_PROJECT);
    } else if (!isLoading && !projectsExist) {
      setScreen(PROJECT_CREATE_PROJECT);
    }
  }, [loading, loadedProjects, initialLoad, products, hasLoadedProjects]);

  useEffect(() => {
    if (products?.length > 0 && projectLoading) {
      setScreen(LOADING);
    } else if (products?.length > 0 && selectedProject?.projectId && !projectLoading) {
      setScreen(PROJECT_SELECT_GROUP);
    }
  }, [projectLoading, selectedProject]);

  const updateSelectedGroupId = (newGroupId) => {
    selectedGroupId.current = newGroupId;
  };

  const singleProduct = [
    {
      itemId, quantity, sku: singleItemSku, itemType: 'catalog'
    }
  ];
  const singleProductAnalytic = singleProduct.map((product) => ({
    quantity: product.quantity,
    sku: product.itemId,
    price: basePrice
  }));

  const multipleProduct = products.map((prod) => {
    return {
      itemId: prod.itemId,
      quantity: prod.quantity,
      sku: prod.sku,
      itemType: 'catalog'
    };
  });
  const multipleProductAnalytic = multipleProduct.map((product) => ({
    quantity: product.quantity,
    sku: product.itemId,
    price: product?.pricing?.value
  }));

  const [addItem] = useLazyDataModel('updateMaterials', {
    variables: {
      listId: defaultListId,
      mutationKey: defaultListMutationKey,
      operation: 'ADD_ITEM',
      groupName: selectedGroup,
      items: isMultiAdd ? multipleProduct : singleProduct
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  const handleAddItemsFromCopyList = async ({ productsGotten }) => {
    const response = await addItem({
      variables: {
        items: productsGotten,
        groupName: 'Unassigned'
      }
    });
    if (response?.data?.updateMaterials?.groupId) {
      setShowSuccessMessage(true);
      setScreen(DEFAULT);
      projectRefetch();
    }
  };

  const [resolveIntent] = useLazyDataModel('updateMaterials', {
    variables: {
      listId: defaultListId,
      mutationKey: defaultListMutationKey,
      operation: 'RESOLVE_INTENT',
      groupId: [productPodItemData?.groupId]
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  const handleResolveIntent = async ({ omsId, sku }) => {
    try {
      const response = await resolveIntent({
        variables: {
          items: [
            {
              id: productPodItemData?.id,
              omsId,
              sku,
            }
          ]
        }
      });
      if (response?.data?.updateMaterials?.groupId) {
        setScreen(DEFAULT);
        projectRefetch();
      }
      handleClose();
    } catch (errorCatched) {
      // eslint-disable-next-line no-console
      console.error('Error resolving intent:', errorCatched);
    }
  };

  const addItemToProject = async ({ type = 'OLD_GROUP', groupName }) => {
    const response = await addItem({
      variables: {
        groupName: groupName || selectedGroup
      }
    });
    if (response?.data?.updateMaterials?.groupId) {
      setShowSuccessMessage(true);
      setScreen(DEFAULT);
      projectRefetch();
    }
    if ((singleProduct?.length > 0 || multipleProduct?.length > 0) && isProjectCreated.current === false) {
      const eventName = type === 'NEW_GROUP' ? ADD_ITEMS_PROJECT_NEW_GROUP : ADD_ITEMS_PROJECT_EXISTING_GROUP;
      const analyticInfo = {
        id: selectedProject?.projectId,
        type: propertyType ? 'y' : 'n',
        desc: projectName ? 'y' : 'n',
        address: addresses?.[0]?.addressLine1 ? 'y' : 'n',
        zip: addresses?.[0]?.zipCode ? 'y' : 'n',
        items: isMultiAdd ? multipleProductAnalytic : singleProductAnalytic
      };
      LIFE_CYCLE_EVENT_BUS.trigger(eventName, analyticInfo);
    }
    return response;
  };

  const handleProjectCreation = async (response) => {
    if (response?.id) {
      setSelectedProject({
        projectId: response?.id,
        projectName: response?.projectName
      });
    }
    isProjectCreated.current = true;
  };

  const onCreateGroup = async (groupName) => {
    setSelectedGroup(groupName);
    const res = await projectRefetch();
    if (res?.data?.projectDetails?.defaultListMutationKey !== defaultListMutationKey) {
      await addItemToProject({ type: 'NEW_GROUP', groupName });
    }
    if (res?.data?.projectDetails?.id) {
      const groups = res.data.projectDetails.itemGroups;
      const group = groups?.find((grp) => grp.groupName === groupName);
      selectedGroupId.current = group?.id || null;
    }
    if (isProjectCreated.current === true) {
      const payload = {
        id: selectedProject?.projectId,
        type: propertyType ? 'y' : 'n',
        desc: projectName ? 'y' : 'n',
        address: addresses?.[0]?.addressLine1 ? 'y' : 'n',
        zip: addresses?.[0]?.zipCode ? 'y' : 'n',
        items: isMultiAdd ? multipleProductAnalytic : singleProductAnalytic
      };
      LIFE_CYCLE_EVENT_BUS.trigger(ADD_ITEM_NEW_PROJECT, payload);
    }
  };

  useEffect(() => {
    if (itemGroups) {
      const newGroupId = itemGroups.find((group) => group.groupName === selectedGroup)?.id;
      if (newGroupId) {
        updateSelectedGroupId(newGroupId);
      }
    }
  }, [selectedGroup]);

  const onSearch = (searchKeyword) => {
    setKeyword(searchKeyword);
  };

  const handleBackPress = () => {
    triggerAnalytics(TRACK.ADD_TO_OVERLAY_EXIT, { screen });
    if (screen === PROJECT_CREATE_PROJECT && !!loadedProjects?.length) {
      setScreen(PROJECT_SELECT_PROJECT);
      return;
    }
    if (addToType === 'Cart' && !!atcDrawerType) {
      setScreen(MINI_PIP_FULL);
      setAtcDrawerType('');
      return;
    }
    if (products?.length === 0 && (screen === PROJECT_SELECT_GROUP || screen === MINI_PIP_BASE)) {
      setScreen(DEFAULT);
      setSelectedGroup(null);
      setSelectedProductDetails({ itemId: null, quantity: null, productData: null });
    }
    if (products?.length > 0 && (screen === PROJECT_SELECT_GROUP || screen === MINI_PIP_BASE)) {
      setScreen(PROJECT_SELECT_PROJECT);
      setSelectedGroup(null);
      setSelectedProductDetails({ itemId: null, quantity: null, productData: null });
    }
    // eslint-disable-next-line max-len
    if (screen === MINI_PIP_PRODUCT_OVERVIEW || screen === MINI_PIP_PRODUCT_SPECS || screen === MINI_PIP_PRODUCT_REVIEWS) {
      setScreen(MINI_PIP_BASE);
    }
    if (screen === MY_STORE || screen === DELIVERY_ZIP) {
      setScreen(DEFAULT);
    }
    if (screen === GENERATED_LIST_INTENTS_PREVIEW) {
      setScreen(previousScreen || DEFAULT);
    }
    if (screen === STARTER_LISTS) {
      setScreen(DEFAULT);
    }
  };

  return (
    <>
      {!screen?.startsWith('COPY_LIST') && (
        <DrawerHeader
          data-testid="add-to-drawer-header"
          title={getDrawerTitle({
            screen,
            showSuccessMessage,
            isIntent,
            HEADER_TEXT,
            DEFAULT
          })}
          onClose={handleClose}
          onBack={shouldShowBack({
            screen,
            addToType,
            PROJECT_SELECT_PROJECT,
            PROJECT_CREATE_PROJECT,
            DEFAULT,
            loadedProjects
          }) && showBackButton ? handleBackPress : null}
        />
      )}
      {screen === LOADING && (
        <AddToLoading />
      )}
      {(screen === DEFAULT || !screen) && (
        <DefaultScreen
          keyword={keyword}
          productPodItemData={productPodItemData}
          onSearch={onSearch}
          handleClose={handleClose}
          projectId={selectedProject.projectId}
          selectedGroup={selectedGroup}
          selectedGroupId={selectedGroupId}
          products={products}
          selectedProject={selectedProject}
          customerType={customerType}
          projectDetails={projectData?.projectDetails}
          isIntent={isIntent}
          handleResolveIntent={handleResolveIntent}
        />
      )}
      {screen === PROJECT_CREATE_PROJECT && (
        <CreateAProjectContent onCreate={handleProjectCreation} handleClose={handleClose} isAddToDrawer />
      )}
      {screen === PROJECT_SELECT_PROJECT && (
        <SelectProject
          products={products}
          projects={loadedProjects}
          projectsExist={!!loadedProjects?.length}
          setSelectedProject={setSelectedProject}
          pageSize={pageSize}
          projectsCount={projectsCount}
          loading={loading}
          setInitialLoad={setInitialLoad}
          setPage={setPage}
          page={page}
        />
      )}
      {screen === PROJECT_SELECT_GROUP && (
        <SelectGroup
          projectId={selectedProject.projectId}
          handleClose={handleClose}
          products={products}
          addItem={addItemToProject}
          onCreateGroup={onCreateGroup}
        />
      )}
      {
        screen?.startsWith('MINI_PIP') && (
          <MiniPip itemId={itemId} isIntent={isIntent} handleResolveIntent={handleResolveIntent} />
        )
      }
      {
        screen?.startsWith('COPY_LIST') && (
          <CopyList
            onClose={handleClose}
            onUpdateProjects={handleAddItemsFromCopyList}
          />
        )
      }
      {
        screen === MY_STORE && (
          <MyStoreForm
            projectId={projectId}
            projectName={projectName}
            assortmentZip={assortmentZip}
            storeId={preferredStore}
            onUpdate={onLocalizationUpdate}
          />
        )
      }
      {
        screen === DELIVERY_ZIP && (
          <DeliveryZipcodeForm
            projectId={projectId}
            projectName={projectName}
            assortmentZipCode={assortmentZip}
            assortmentZipId={assortmentZipId}
            preferredStore={preferredStore}
            onUpdate={onLocalizationUpdate}
          />
        )
      }
      {
        screen === GENERATED_LIST_INTENTS_PREVIEW && (
          <QueryProvider cacheKey="intents-preview">
            <IntentsPreview
              onNewIntentsSave={aiListBuilderData.onNewIntentsSave}
              refId={aiListBuilderData.refId}
              refType={aiListBuilderData.refType}
            />
          </QueryProvider>
        )
      }
      {
        screen === STARTER_LISTS && (
          <StarterLists aiListBuilderData={aiListBuilderData} />
        )
      }
    </>
  );
};

AddToProject.displayName = 'AddToProject';

AddToProject.propTypes = {
  projectId: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  initialLoad: PropTypes.bool.isRequired,
  setInitialLoad: PropTypes.func.isRequired,
  showBackButton: PropTypes.bool,
  productPodItemData: PropTypes.shape({
    itemId: PropTypes.string,
    id: PropTypes.string,
    quantity: PropTypes.number,
    searchKeyword: PropTypes.string,
    groupId: PropTypes.string
  }),
  aiListBuilderData: PropTypes.shape({
    onNewIntentsSave: PropTypes.func,
    title: PropTypes.string,
    refId: PropTypes.string,
    refType: PropTypes.string,
    materials: PropTypes.arrayOf(PropTypes.shape({
      groupName: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.shape({
        quantity: PropTypes.number,
        product: PropTypes.string,
      }))
    }))
  }),
};

AddToProject.defaultProps = {
  projectId: null,
  products: [],
  showBackButton: true,
  productPodItemData: {},
  aiListBuilderData: {}
};

AddToProject.dataModel = extend({
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      brandName: string(),
      productType: string(),
      productLabel: string(),
      storeSkuNumber: string(),
      omsThdSku: string(),
      specialOrderSku: string()
    }),
    info: shape({
      productDepartment: string(),
      classNumber: string()
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        type: string(),
        subType: string()
      }))
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  })
},
{
  updateMaterials: params({
    listId: string().isRequired(),
    mutationKey: string().isRequired(),
    operation: customType('MaterialsOperationType').enum(
      ['EDIT_GROUP_NAME', 'DELETE_GROUP', 'ADD_ITEM', 'DELETE_ITEM', 'UPDATE_QUANTITY', 'REARRANGE']
    ),
    groupName: string(),
    groupId: arrayOf(string()),
    items: arrayOf(customType('Items').shape({
      itemId: string(),
      id: string(),
      omsId: string(),
      quantity: number(),
      sku: string(),
      itemType: string()
    }))
  }).mutation().shape({
    groupId: string()
  })
},
DefaultScreen,
SelectGroup,
projectsModel,
CreateAProjectContent,
DeliveryZipcodeForm,
MyStoreForm,
useAddNewIntentsProject,
projectDetailsModel
);
